import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, map, Observable, from } from 'rxjs';
import { APIService } from 'src/app/services/api.service';
import { LoggingService } from 'src/app/services/logging.service';
import { Task } from '../models/task.model';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  constructor(
    private readonly apiService: APIService,
    private readonly logger: LoggingService,
  ) {}

  public loadTask(taskId: string): Observable<Task> {
    this.loadingSubject.next(true);

    return this.loadTaskItem(taskId).pipe(
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  public loadTasks(filters: unknown[]): Observable<Map<number, Task[]>> {
    this.loadingSubject.next(true);

    return from(this.apiService.getCollection({ collection: 'tasks', filters })).pipe(
      map((tasks) => {
        const allTasks = new Map<number, Array<Task>>();

        tasks.forEach((task: Task) => {
          if (task.dueDate) {
            const taskDate = new Date(task.dueDate);
            const year = taskDate.getFullYear();
            const month = taskDate.getMonth();
            const day = taskDate.getDate();
            const currentDate = new Date(year, month, day);

            if (allTasks.has(currentDate.getTime())) {
              allTasks.get(currentDate.getTime())?.push(task);
            } else {
              allTasks.set(currentDate.getTime(), [task]);
            }
          }
        });

        return allTasks;
      }),
      finalize(() => {
        this.loadingSubject.next(false);
      }),
    );
  }

  public saveTask(currentTask: Task): Observable<Task> {
    this.savingSubject.next(true);

    if (currentTask.completed && !currentTask.completedOn) {
      currentTask.completedOn = Date.now();
    } else if (!currentTask.completed) {
      currentTask.completedOn = 0;
    }

    return this.saveTaskItem(currentTask).pipe(
      finalize(() => {
        this.savingSubject.next(false);
      }),
    );
  }

  public deleteTask(task: Task): Observable<boolean> {
    this.deletingSubject.next(true);

    return this.deleteTaskItem(task._id).pipe(
      map(() => {
        return true;
      }),
      finalize(() => {
        this.deletingSubject.next(false);
      }),
    );
  }

  public tasksFor(day: Date): Observable<Array<Task>> {
    this.loadingTasksSubject.next(true);
    const userEmail = (this.apiService.user as any).attributes.email;

    const year = day.getFullYear();
    const month = day.getMonth();
    const date = day.getDate();

    const start = new Date(year, month, date).setHours(0, 0, 0);
    const end = new Date(year, month, date).setHours(23, 59, 59);

    const filters = [
      { field: 'users.Email', operator: 'eq', value: userEmail },
      { field: 'dueDate', operator: 'between', value: [start, end] },
    ];

    return from(this.apiService.getCollection({ collection: this.collectionName, filters })).pipe(
      finalize(() => {
        this.loadingTasksSubject.next(false);
      }),
    );
  }

  private loadTaskItem(id: string): Observable<Task> {
    return from(this.apiService.getItem(this.collectionName, id)).pipe(
      map((task) => {
        return {
          _id: task._id,
          name: task.name,
          description: task.description,
          comments: task.comments,
          dueDate: new Date(task.dueDate),
          completed: task.completed,
          completedOn: task.completedOn,
          lastModified: task.lastModified,
          users: task.users,
          amount: task.amount,
        };
      }),
    );
  }

  private saveTaskItem(task: Task): Observable<Task> {
    task.dueDate = task.dueDate ? new Date(task.dueDate).getTime() : 0;

    if (!task._id) {
      delete task._id;
      return from(this.apiService.createCollectionItem(this.collectionName, task));
    } else {
      return from(this.apiService.saveCollectionItem(this.collectionName, task));
    }
  }

  private deleteTaskItem(id: string | undefined): Observable<void> {
    if (!id) {
      throw new Error('Id is mandatory!');
    }

    return from(this.apiService.deleteCollectionItem(this.collectionName, id ?? ''));
  }

  private readonly collectionName = 'tasks';
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly loadingTasksSubject = new BehaviorSubject<boolean>(false);
  private readonly savingSubject = new BehaviorSubject<boolean>(false);
  private readonly deletingSubject = new BehaviorSubject<boolean>(false);

  public loading$ = this.loadingSubject.asObservable();
  public loadingTasks$ = this.loadingTasksSubject.asObservable();
  public saving$ = this.savingSubject.asObservable();
  public deleting$ = this.deletingSubject.asObservable();
}
